import moment from 'moment-timezone';
import {
  getDecisionNodeAtPath,
  getItemAndDiscountPeriodMatches,
  getSelectedDiscountPeriod,
  getSelectedOrderFromOrdersSearch,
} from '../../../extractFromState';
import { addEdgeToDecisionTreePath, addSnippetText } from '../../searchActions';
import {
  calculateItemRefundForPriceMatch,
  getItemName,
  getOrderShipmentFromOrderShipmentsList,
  UnhandledDecisionTreeCase,
} from '../../../util';
import { decisionTreeCaseNotHandled } from '../../index';
import {
  getOrderingSystem,
  getOrderShipmentsOfSelectedOrder,
  getShippedItemsByShipmentStatusForShopify,
} from '../../../selectors/tools';
import { showErrorPopup } from '../../../components/popups/ErrorPopup/ErrorPopup';
import * as ordersActions from '../OrdersSearchActions';
import { replaceTemplatedTextForMultipleItems } from '../../../selectors/templating';

export const loadInfoForPriceMatch = () => {
  return (dispatch, getState) => {
    const state = getState();
    try {
      const selectedDiscountPeriod = getSelectedDiscountPeriod(state);
      if (selectedDiscountPeriod.couponCode) {
        const now = moment().tz('America/Chicago');
        const tooLate = now.isAfter(moment(selectedDiscountPeriod.gracePeriodEndDate).tz('America/Chicago'), 'day');
        if (tooLate) {
          return dispatch(addEdgeToDecisionTreePath('no_coupon_code'));
        }

        return dispatch(addEdgeToDecisionTreePath('coupon_code'));
      }

      return dispatch(addEdgeToDecisionTreePath('no_coupon_code'));
    } catch (err) {
      if (err instanceof UnhandledDecisionTreeCase) {
        return dispatch(decisionTreeCaseNotHandled(err.message));
      }
      throw err;
    }
  };
};

export const loadInfoForItemPriceMatch = () => {
  return async (dispatch, getState) => {
    const state = getState();
    const orderingSystem = getOrderingSystem(state);
    if (orderingSystem !== 'shopify') {
      return showErrorPopup(`${orderingSystem} is not supported`, 'Error');
    }

    dispatch(ordersActions.clearSnippetText());
    try {
      const orderShipments = getOrderShipmentsOfSelectedOrder(state);
      const itemsByShipmentStatus = getShippedItemsByShipmentStatusForShopify(state);
      const itemAndDiscountPeriodMatches = getItemAndDiscountPeriodMatches(state);
      const selectedOrder = getSelectedOrderFromOrdersSearch(state);
      const decisionNode = getDecisionNodeAtPath(state);
      const alwaysCheckPeriodDates = decisionNode.alwaysCheckPeriodDates;
      let totalRefundAmount = 0.0;
      let totalItemsProcessed = 0;
      let numItemsOutsideGracePeriod = 0;

      const paramsForNegativeSnippets = [];
      const paramsForPositiveSnippets = [];
      let positiveSnippetText = '';
      let negativeSnippetText = '';

      // iterate through each shipment status and add each relevant snippet
      for (let shipmentStatus in itemsByShipmentStatus) {
        if (itemsByShipmentStatus.hasOwnProperty(shipmentStatus)) {
          const lineItems = itemsByShipmentStatus[shipmentStatus];
          const shipmentData = JSON.parse(shipmentStatus);
          const { shipmentId } = shipmentData;
          const orderShipment = getOrderShipmentFromOrderShipmentsList(orderingSystem, orderShipments, shipmentId);
          // if there is an orderShipment, we can assume item was fulfilled
          if (orderShipment || alwaysCheckPeriodDates) {
            // TODO: Don't make functions within a loop
            // eslint-disable-next-line
            lineItems.forEach((item) => {
              totalItemsProcessed++;
              const params = {};
              const itemName = getItemName(item, orderingSystem);
              const discountPeriod = itemAndDiscountPeriodMatches[item.id];
              const createdAt = moment(selectedOrder.created_at).tz('America/Chicago');
              const tooLate = createdAt.isAfter(moment(discountPeriod.gracePeriodEndDate).tz('America/Chicago'), 'day');
              const tooEarly = createdAt.isBefore(
                moment(discountPeriod.gracePeriodStartDate).tz('America/Chicago'),
                'day'
              );
              params.item = itemName;
              // if discount period valid
              if (discountPeriod && discountPeriod.title !== 'none') {
                // within grace period
                if (!tooLate && !tooEarly) {
                  const itemDiscount = calculateItemRefundForPriceMatch(discountPeriod, item);
                  totalRefundAmount += itemDiscount;
                  const text = decisionNode.positiveSnippetText && decisionNode.positiveSnippetText[0];
                  params.amount = `$${parseFloat(itemDiscount).toFixed(2)}`;

                  // gather snippet text and params to be combined with the same snippets later
                  paramsForPositiveSnippets.push(params);
                  positiveSnippetText = text;

                  // add to count - which will escalate to specialists
                } else {
                  numItemsOutsideGracePeriod++;
                }
                // else deny refund
              } else {
                // snippet
                const text = decisionNode.negativeSnippetText && decisionNode.negativeSnippetText[1];

                // gather snippet text and params to be combined with the same snippets later
                paramsForNegativeSnippets.push(params);
                negativeSnippetText = text;
              }
            });
          } else {
            // if order is unfulfilled, check to see if refund applies
            // TODO: Don't make functions within a loop
            // eslint-disable-next-line
            lineItems.forEach((item) => {
              totalItemsProcessed++;
              const params = {};
              const itemName = getItemName(item, orderingSystem);
              const discountPeriod = itemAndDiscountPeriodMatches[item.id];
              params.item = itemName;
              // if so, calculate refund send macro
              if (discountPeriod && discountPeriod.title !== 'none') {
                const itemDiscount = calculateItemRefundForPriceMatch(discountPeriod, item);
                totalRefundAmount += itemDiscount;
                const text = decisionNode.positiveSnippetText && decisionNode.positiveSnippetText[0];
                params.amount = `$${parseFloat(itemDiscount).toFixed(2)}`;

                // gather snippet text and params to be combined with the same snippets later
                paramsForPositiveSnippets.push(params);
                positiveSnippetText = text;
              } else {
                // snippet
                const text = decisionNode.negativeSnippetText && decisionNode.negativeSnippetText[1];

                // gather snippet text and params to be combined with the same snippets later
                paramsForNegativeSnippets.push(params);
                negativeSnippetText = text;
              }
            });
          }
        }
      }

      // combine snippets into one
      const positiveSnippetsCombined = replaceTemplatedTextForMultipleItems(
        positiveSnippetText,
        paramsForPositiveSnippets
      );
      const negativeSnippetsCombined = replaceTemplatedTextForMultipleItems(
        negativeSnippetText,
        paramsForNegativeSnippets
      );

      // add combined snippets to the store
      positiveSnippetsCombined && dispatch(addSnippetText(positiveSnippetsCombined));
      negativeSnippetsCombined && dispatch(addSnippetText(negativeSnippetsCombined));

      if (numItemsOutsideGracePeriod > 0) {
        return dispatch(addEdgeToDecisionTreePath('outside_grace_period'));
      } else if (totalRefundAmount > 0.0) {
        dispatch(ordersActions.setRefundAmount(totalRefundAmount.toFixed(2)));
        dispatch(addEdgeToDecisionTreePath('can_price_match'));
      } else {
        dispatch(ordersActions.setRefundAmount(0.0));
        if (totalItemsProcessed > 1) {
          return dispatch(addEdgeToDecisionTreePath('multiple_items_no_discount'));
        }

        return dispatch(addEdgeToDecisionTreePath('no_discount_available'));
      }
    } catch (err) {
      if (err instanceof UnhandledDecisionTreeCase) {
        return dispatch(decisionTreeCaseNotHandled(err.message));
      }
      throw err;
    }
  };
};
